import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import { sanitizeSalesCategory } from "api-types/builders/salesCategory";
import type { SalesCategorySizes } from "api-types/builders/salesCategoryTypes";
import type { ClientLocationPlanBaseData } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLotCommunityPlanHydrated } from "api-types/lotCommunityPlans/lotCommunityPlansResponses";
import type { ClientLotHydrated } from "api-types/lots/lotsResponses";
import type { Doc } from "building-types/doc";
import type { DocOption } from "building-types/options/optionSet";
import type {
  OptionOverrides,
  OptionSetOverride,
  OptionSetOverrides,
} from "building-types/options/optionTemplateTypes";
import { SalesCategory } from "common/types/salesCategoryTypesEnum";
import { formatPricePlusMinus } from "common/utils/formatDollars";
import objectIsEmpty from "common/utils/objectIsEmpty";
import shallowCompare from "common/utils/shallowCompare";
import { sortEntitiesBySortIndex } from "common/utils/sortIndex";
import type { Mutable } from "common/utils/types";
import { optionPrice } from "doc/options/optionSetTools";
import { removeOrphanedInteriorPaletteOptionSet } from "doc/options/paletteOptionSet";
import { isElevationOptionSet } from "doc/options/systemOptionSet";
import { SystemOptionSet } from "doc/options/SystemOptionSetEnum";
import type { NonZeroBasePriceOptions } from "lib/components/builders/ConfirmNonZeroBasePriceOptions";
import { getTbdPriceLabel, renderDisplayPrice } from "lib/components/helpers/getOptionPriceDisplay";
import {
  getBuilderLocationPlanURL,
  getBuilderLotURL,
  getLotCommunityPlanByCommunityPlanIdURL,
} from "lib/endpointHelpers";
import { fetcher, useFetch } from "lib/fetcher";
import {
  getPrimarySalesCategoryCounts,
  getPrimarySalesKeyToNameMapping,
  getSecondaryToPrimaryNameMapping,
} from "lib/state/salesCategory";
import { useMemo } from "react";
import {
  type PlanLibraryOptionSet,
  type PlanLibraryOptionSets,
  type PlanLibraryOptionSetsPerSalesCategory,
  type PlanLibraryState,
  PlanLibraryStateType,
} from "./planLibraryTypes";

function withOrphanedInteriorPaletteOptionSetsRemoved(data: ClientLocationPlanBaseData): ClientLocationPlanBaseData {
  return {
    ...data,
    optionSets: removeOrphanedInteriorPaletteOptionSet(data.optionSets),
    masterPlan: {
      ...data.masterPlan,
      doc: {
        ...data.masterPlan.doc,
        optionSets: removeOrphanedInteriorPaletteOptionSet(data.masterPlan.doc.optionSets),
      },
    },
  };
}

export function useFetchPlanLibraryLot(
  builderId: string,
  communityId: string,
  lotId: string,
): ClientLotHydrated | null {
  const { data } = useFetch<ClientLotHydrated>(
    getBuilderLotURL({
      builderId: builderId,
      locationId: communityId,
      lotId: lotId,
    }),
    [builderId, communityId, lotId],
  );
  return data ?? null;
}

export async function planLibraryFetchLotCommunityPlan(
  builderId: string,
  locationId: string,
  lotId: string,
  communityPlanId: string,
): Promise<ClientLotCommunityPlanHydrated | null> {
  try {
    const plan = await fetcher<ClientLotCommunityPlanHydrated>(
      getLotCommunityPlanByCommunityPlanIdURL({ builderId, locationId: locationId, communityPlanId, lotId }),
    );
    return plan;
  } catch (e) {
    console.log(e);
    return null;
  }
}

export function useFetchLotCommunityPlan({
  builderId,
  locationId,
  lotId,
  communityPlanId,
}: {
  builderId: string;
  locationId: string;
  lotId: string;
  communityPlanId: string;
}): ClientLotCommunityPlanHydrated | null {
  const { data } = useFetch<ClientLotCommunityPlanHydrated>(
    getLotCommunityPlanByCommunityPlanIdURL({
      builderId: builderId,
      locationId: locationId,
      communityPlanId: communityPlanId,
      lotId: lotId,
    }),
    [builderId, locationId, lotId, communityPlanId],
  );
  return data ?? null;
}

export async function planLibraryFetchCommunityPlanBaseData({
  builderId,
  locationId,
  locationPlanId,
}: {
  builderId: string;
  locationId: string;
  locationPlanId: string;
}): Promise<ClientLocationPlanBaseData | null> {
  try {
    const data = await fetcher<ClientLocationPlanBaseData>(
      getBuilderLocationPlanURL({ builderId, locationId, locationPlanId }),
    );
    return withOrphanedInteriorPaletteOptionSetsRemoved(data);
  } catch (e) {
    console.log(e);
    return null;
  }
}

export function useFetchCommunityPlanBaseData({
  builderId,
  locationId,
  locationPlanId,
}: {
  builderId: string;
  locationId: string;
  locationPlanId: string;
}): ClientLocationPlanBaseData | null {
  const { data } = useFetch<ClientLocationPlanBaseData>(
    getBuilderLocationPlanURL({
      builderId: builderId,
      locationId: locationId,
      locationPlanId: locationPlanId,
    }),
    [builderId, locationId, locationPlanId],
  );
  return useMemo(() => (data ? withOrphanedInteriorPaletteOptionSetsRemoved(data) : null), [data]);
}

function planLibraryCreateOptionOverride(
  oldOptionSet: PlanLibraryOptionSet,
  newOptionSet: PlanLibraryOptionSet,
  oldOverride: OptionSetOverride,
  optionId: string,
): OptionOverrides | null {
  let newOptionOverrides: Mutable<OptionOverrides> = oldOverride?.options?.[optionId] ?? {};
  const oldOption = oldOptionSet.options.find(({ id }) => id === optionId);
  const newOption = newOptionSet.options.find(({ id }) => id === optionId);
  if (oldOption && newOption) {
    const keys: (keyof DocOption)[] = [
      "name",
      "price",
      "priceTbd",
      "disabled",
      "sortIndex",
      "builderOptionCode",
      "description",
    ];
    for (const k of keys) {
      if (newOption[k] !== oldOption[k] && newOption[k] !== undefined) {
        newOptionOverrides = { ...newOptionOverrides, [k]: newOption[k] };
      }
    }

    if (newOption.salesImages) {
      if (newOption.salesImages.length === 0 && oldOption.salesImages && oldOption.salesImages.length > 0) {
        const { salesImages, ...rest } = newOptionOverrides;
        newOptionOverrides = rest;
      } else if (
        oldOption.salesImages === undefined ||
        newOption.salesImages.length > oldOption.salesImages.length ||
        newOption.salesImages.length < oldOption.salesImages.length
      ) {
        newOptionOverrides = { ...newOptionOverrides, salesImages: newOption.salesImages };
      } else {
        for (let i = 0; i < newOption.salesImages.length; ++i) {
          if (!shallowCompare(newOption.salesImages[i], oldOption.salesImages[i])) {
            newOptionOverrides = { ...newOptionOverrides, salesImages: newOption.salesImages };
            break;
          }
        }
      }
    }
  }

  if (!objectIsEmpty(newOptionOverrides)) {
    return newOptionOverrides;
  }

  return null;
}

export function planLibraryCreateOptionSetOverride(
  oldOptionSet: PlanLibraryOptionSet,
  newOptionSet: PlanLibraryOptionSet,
  oldOverride?: OptionSetOverride,
): OptionSetOverride | null {
  let newOverride: OptionSetOverride = oldOverride ? { ...oldOverride } : {};

  if (oldOptionSet.id === newOptionSet.id) {
    const keys: (keyof PlanLibraryOptionSet)[] = [
      "name",
      "disabled",
      "sortIndex",
      "defaultValue",
      "salesCategory",
      "builderOptionSetCode",
    ];
    for (const k of keys) {
      if (newOptionSet[k] !== oldOptionSet[k] && newOptionSet[k] !== undefined) {
        newOverride = { ...newOverride, [k]: newOptionSet[k] };
      }
    }

    for (const { id } of newOptionSet.options) {
      const newOptionOverride = planLibraryCreateOptionOverride(oldOptionSet, newOptionSet, newOverride, id);
      if (newOptionOverride) {
        newOverride = {
          ...newOverride,
          options: {
            ...newOverride.options,
            [id]: newOptionOverride,
          },
        };
      } else if (newOverride.options?.[id]) {
        delete newOverride.options[id];
      }
    }

    if (!objectIsEmpty(newOverride)) {
      return newOverride;
    }
  }

  return null;
}

export function planLibraryCreateOptionSets(
  optionSets: Doc["optionSets"],
  builder: ClientBuilder,
): PlanLibraryOptionSets {
  const hierarchy = builder.salesConfig?.salesCategoryCustomization?.hierarchy;
  const planLibraryOptionSets: PlanLibraryOptionSets = {};

  for (const {
    id,
    builderOptionSetCode = "",
    name = "",
    disabled = false,
    defaultValue = "",
    salesCategory = SalesCategory.UNCATEGORIZED,
    description = "",
    options = {},
  } of Object.values(optionSets)) {
    const optionsArray = Object.entries(options).map(([k, o]) => {
      const {
        name = "",
        priceTbd = false,
        disabled = false,
        salesImages = [],
        builderOptionCode = "",
        description = "",
      } = o; // don't allow undefined
      return {
        ...o,
        id: k, // replace id with key otherwise it will never match defaultValue in option set
        name,
        priceTbd,
        price: optionPrice(o),
        disabled,
        salesImages,
        builderOptionCode,
        description,
      };
    });

    const isBooleanOptionSet =
      options["true"] !== undefined && options["false"] !== undefined && optionsArray?.length === 2;

    planLibraryOptionSets[id] = {
      ...optionSets[id],
      name,
      disabled,
      builderOptionSetCode,
      defaultValue,
      salesCategory: sanitizeSalesCategory(salesCategory, hierarchy),
      description,
      isBooleanOptionSet,
      isFilteredBySearch: false,
      options: sortEntitiesBySortIndex(optionsArray),
      ruleRefsByOption: {},
    };
  }

  return planLibraryOptionSets;
}

export function planLibraryCreatePrimarySalesCategoryData(
  salesCategoryNames: string[],
  salesCategorySizes: SalesCategorySizes,
  builder: ClientBuilder,
): {
  primarySalesCategoryCounts: Record<string, number>;
  primarySalesKeyToNameMap: Record<string, string>;
  secondaryToPrimarySalesCategoryMap: Record<string, string>;
} {
  const primarySalesCategoryCounts = getPrimarySalesCategoryCounts(
    salesCategoryNames,
    salesCategorySizes,
    builder.salesConfig?.salesCategoryCustomization?.hierarchy,
  );
  const primarySalesKeyToNameMap = getPrimarySalesKeyToNameMapping(
    builder.salesConfig?.salesCategoryCustomization?.hierarchy.primaryLevels,
    true,
  );

  const secondaryToPrimarySalesCategoryMap = getSecondaryToPrimaryNameMapping(
    builder.salesConfig?.salesCategoryCustomization?.hierarchy,
    true,
  );
  return {
    primarySalesCategoryCounts,
    primarySalesKeyToNameMap,
    secondaryToPrimarySalesCategoryMap,
  };
}

/**
 * @param inheritedOptionSets
 * @param optionSets
 * @param salesCategoryNames list of all secondary sales category (retrieved from salesCategoryNamesInHierarchy)
 * @param builder
 * @returns
 */
export function planLibraryCreateSalesCategories(
  inheritedOptionSets: Doc["optionSets"],
  optionSets: Doc["optionSets"],
  salesCategoryNames: string[],
  builder: ClientBuilder,
): {
  salesCategorySizes: SalesCategorySizes;
  salesCategoryNames: string[];
  primarySalesCategorySizes: SalesCategorySizes;
  primarySalesCategoryNames: string[];
  primarySalesCategoryKeyToNameMap: Record<string, string>;
  secondaryToPrimarySalesCategoryMap: Record<string, string>;
  booleanOptionSetsPerSalesCategory: PlanLibraryOptionSetsPerSalesCategory;
  nonBoolOptionSetsPerSalesCategory: PlanLibraryOptionSetsPerSalesCategory;
  elevationOptionSet: PlanLibraryOptionSet | null;
} {
  let elevationOptionSet: PlanLibraryOptionSet | null = null;
  const filteredOptionSets: PlanLibraryOptionSet[] = [];
  const booleanOptionSetsPerSalesCategory: PlanLibraryOptionSetsPerSalesCategory = {};
  const nonBoolOptionSetsPerSalesCategory: PlanLibraryOptionSetsPerSalesCategory = {};

  for (const category of salesCategoryNames) {
    booleanOptionSetsPerSalesCategory[category] = [];
    nonBoolOptionSetsPerSalesCategory[category] = [];
  }

  if (builder.salesConfig?.optionsDisplayConfig?.displayDisabled === true) {
    for (const x of Object.values(planLibraryCreateOptionSets(optionSets, builder))) {
      if (isElevationOptionSet(x.id)) {
        elevationOptionSet = x;
      } else {
        filteredOptionSets.push(x);
      }
    }
  } else {
    for (const x of Object.values(planLibraryCreateOptionSets(optionSets, builder))) {
      const inheritedOptionSet = inheritedOptionSets[x.id];

      // This shouldn't happen but added for robustness
      if (!inheritedOptionSet) {
        continue;
      }

      x.options = x.options.filter(({ id }) => {
        if (!inheritedOptionSet.options[id]) {
          return false;
        }
        return !inheritedOptionSet.options[id].disabled;
      });
      if (isElevationOptionSet(x.id)) {
        elevationOptionSet = x;
      } else if (!inheritedOptionSet.disabled) {
        if (x.isBooleanOptionSet) {
          if (x.options.length > 1) {
            filteredOptionSets.push(x);
          }
        } else {
          if (x.options.length > 0) {
            filteredOptionSets.push(x);
          }
        }
      }
    }
  }

  filteredOptionSets.forEach((x) => {
    if (x.isBooleanOptionSet) {
      booleanOptionSetsPerSalesCategory[x.salesCategory].push(x);
    } else {
      nonBoolOptionSetsPerSalesCategory[x.salesCategory].push(x);
    }
  });

  for (const [category, booleanOptionSets] of Object.entries(booleanOptionSetsPerSalesCategory)) {
    booleanOptionSetsPerSalesCategory[category] = sortEntitiesBySortIndex(booleanOptionSets);
  }
  for (const [category, nonBoolOptionSets] of Object.entries(nonBoolOptionSetsPerSalesCategory)) {
    nonBoolOptionSetsPerSalesCategory[category] = sortEntitiesBySortIndex(nonBoolOptionSets);
  }

  const salesCategorySizes = salesCategoryNames.reduce((sizes, category) => {
    const a = booleanOptionSetsPerSalesCategory[category].length;
    const b = nonBoolOptionSetsPerSalesCategory[category].length;
    sizes[category] = a + b;
    return sizes;
  }, {});

  const {
    primarySalesCategoryCounts: primarySalesCategorySizes,
    primarySalesKeyToNameMap: primarySalesCategoryKeyToNameMap,
    secondaryToPrimarySalesCategoryMap,
  } = planLibraryCreatePrimarySalesCategoryData(salesCategoryNames, salesCategorySizes, builder);

  const primarySalesCategoryNames = Object.keys(primarySalesCategorySizes);

  return {
    salesCategorySizes,
    salesCategoryNames,
    primarySalesCategorySizes,
    primarySalesCategoryNames,
    primarySalesCategoryKeyToNameMap,
    secondaryToPrimarySalesCategoryMap,
    booleanOptionSetsPerSalesCategory,
    nonBoolOptionSetsPerSalesCategory,
    elevationOptionSet,
  };
}

/**
 * On child pages, pushes option sets whose salesCategory was
 * overridden by a parent level — but not claimed by the current level — to
 * the end of their category. This ensures parent-recategorized items appear
 * at the bottom rather than interleaved with native category members.
 */
export function pushParentMovedItemsToEnd(
  categoryMap: Mutable<PlanLibraryOptionSetsPerSalesCategory>,
  overrides: OptionSetOverrides,
  parentSalesCategoryMap: Record<string, string>,
): void {
  for (const [category, optionSets] of Object.entries(categoryMap)) {
    const kept: PlanLibraryOptionSet[] = [];
    const parentMoved: PlanLibraryOptionSet[] = [];

    for (const os of optionSets) {
      const isParentMoved = !overrides[os.id]?.salesCategory && parentSalesCategoryMap[os.id] !== undefined;

      if (isParentMoved) {
        parentMoved.push(os);
      } else {
        kept.push(os);
      }
    }

    if (parentMoved.length > 0) {
      categoryMap[category] = [...kept, ...parentMoved];
    }
  }
}

function removeEmpties(overrides: OptionSetOverrides, optionSetId: string) {
  const options = overrides[optionSetId]?.options;
  if (options) {
    Object.entries(options).forEach(([k, v]) => {
      if (objectIsEmpty(v)) {
        delete options[k];
      }
    });

    if (objectIsEmpty(options)) {
      overrides[optionSetId] = Object.entries(overrides[optionSetId]).reduce((obj, [k, v]) => {
        if (k !== "options") {
          obj[k] = v;
        }
        return obj;
      }, {});
    }
  }

  if (objectIsEmpty(overrides[optionSetId])) {
    delete overrides[optionSetId];
  }
}

export function planLibraryGetInheritedAndCurrentPrice(
  builder: ClientBuilder,
  inheritedOptionSet: PlanLibraryOptionSet,
  optionSetOverride: OptionSetOverride,
  option: DocOption,
) {
  const customTbdPriceLabel = builder.salesConfig?.optionsDisplayConfig?.tbdPriceLabel;
  const inheritedOption = inheritedOptionSet.options?.find(({ id }) => id === option.id);
  const inheritedPrice = typeof inheritedOption?.price !== "object" ? inheritedOption?.price : undefined;
  const currentPriceVal = typeof option?.price !== "object" ? option?.price : undefined;

  const isBaseOption = (optionSetOverride?.defaultValue ?? inheritedOptionSet.defaultValue) === option.id;
  const tbdLabel = getTbdPriceLabel(customTbdPriceLabel);

  return {
    inheritPrice: renderDisplayPrice(
      { value: inheritedPrice, tbd: inheritedOption?.priceTbd },
      isBaseOption,
      tbdLabel,
      "Included",
      true,
    ),
    currentPrice: renderDisplayPrice(
      { value: currentPriceVal, tbd: option?.priceTbd },
      isBaseOption,
      tbdLabel,
      "Included",
      true,
    ),
  };
}

export function planLibraryDiscardOptionPropOverride(
  overrides: OptionSetOverrides,
  optionSetId: string,
  optionId: string,
  key: string,
) {
  if (key !== "sortIndex" && overrides?.[optionSetId]?.options?.[optionId]?.[key] !== undefined) {
    // don't discard sort indices otherwise random sort chaos will result
    delete overrides[optionSetId].options[optionId][key];
    removeEmpties(overrides, optionSetId);
    return true;
  }
  return false;
}

export function planLibraryDiscardOptionSetPropOverride(
  overrides: OptionSetOverrides,
  optionSetId: string,
  key: string,
) {
  if (!nonOverrideKeys.includes(key) && key !== "options" && overrides?.[optionSetId]?.[key] !== undefined) {
    // don't discard sort indices otherwise random sort chaos will result
    delete overrides[optionSetId][key];
    removeEmpties(overrides, optionSetId);
    return true;
  }
  return false;
}

export function planLibraryIsOptionOverridden(optionOverrides: OptionOverrides | undefined) {
  for (const key of Object.keys(optionOverrides ?? {})) {
    if (key !== "sortIndex") {
      // do not indicate overridden sort indices as overridden in the UI
      return true;
    }
  }
  return false;
}

// These keys do not indicate as overridden in the UI
const nonOverrideKeys = ["sortIndex", "showroomIntro", "showroomIntroOverrides"];

export function planLibraryIsOptionSetOverridden(
  optionSetOverride: OptionSetOverride,
  optionSet: PlanLibraryOptionSet,
) {
  for (const key of Object.keys(optionSetOverride ?? {})) {
    switch (key) {
      case "options": {
        const options = optionSetOverride.options ?? {};
        for (const optionId in options) {
          // Ensure option exists on optionSet
          if (
            optionSet.options.find((opt) => opt.id === optionId) &&
            planLibraryIsOptionOverridden(options[optionId])
          ) {
            return true;
          }
        }
        break;
      }
      default: {
        if (!nonOverrideKeys.includes(key)) {
          return true;
        }
        break;
      }
    }
  }
  return false;
}

export function planLibraryOptionSetWithOverrides(
  optionSet: PlanLibraryOptionSet,
  optionSetOverride: OptionSetOverride = {},
): PlanLibraryOptionSet {
  const { options: overrideOptions, ...overrideProps } = optionSetOverride;
  const options: DocOption[] = [];
  for (const option of optionSet.options) {
    const overrideOption = overrideOptions?.[option.id] ?? {};
    options.push({ ...option, ...overrideOption });
  }
  return { ...optionSet, ...overrideProps, options };
}

export function planLibraryGetNonZeroBasePriceOptions(
  nonBoolOptionSetsPerSalesCategory: PlanLibraryOptionSetsPerSalesCategory,
  booleanOptionSetsPerSalesCategory: PlanLibraryOptionSetsPerSalesCategory,
) {
  const options: NonZeroBasePriceOptions = [];

  Object.values(nonBoolOptionSetsPerSalesCategory).forEach((nonBoolOptionSetsInSalesCategory) => {
    for (const optionSet of nonBoolOptionSetsInSalesCategory) {
      const opt = optionSet.options.find(({ id }) => id === optionSet.defaultValue);
      const optPrice = typeof opt?.price !== "object" ? opt?.price : undefined;
      if (opt && !opt.priceTbd && optPrice !== undefined && optPrice !== 0) {
        options.push({ label: `${optionSet.name} - ${opt.name}`, price: formatPricePlusMinus(optPrice) });
      }
    }
  });
  Object.values(booleanOptionSetsPerSalesCategory).forEach((booleanOptionSetsInSalesCategory) => {
    for (const optionSet of booleanOptionSetsInSalesCategory) {
      const opt = optionSet.options.find(({ id }) => id === optionSet.defaultValue);
      const optPrice = typeof opt?.price !== "object" ? opt?.price : undefined;
      if (opt && !opt.priceTbd && optPrice !== undefined && optPrice !== 0) {
        options.push({ label: `${optionSet.name} - ${opt.name}`, price: formatPricePlusMinus(optPrice) });
      }
    }
  });

  return options;
}

/** Adds the building's elevation option set back to option sets narrowed to one reporting unit. */
export function withElevationOptionSet(
  unitLevelOptionSets: Doc["optionSets"],
  allOptionSets: Doc["optionSets"],
): Doc["optionSets"] {
  const elevationOptionSet = allOptionSets[SystemOptionSet.ELEVATION_STYLE];
  if (!elevationOptionSet) {
    return unitLevelOptionSets;
  }
  return { ...unitLevelOptionSets, [SystemOptionSet.ELEVATION_STYLE]: elevationOptionSet };
}

export function getPlanLibraryImages(state: PlanLibraryState) {
  switch (state.state) {
    case PlanLibraryStateType.UNINITIALIZED:
      return undefined;
    case PlanLibraryStateType.INITIALIZED: {
      switch (state.plans.planType) {
        case "community_plan":
        case "lot":
          return state.plans.locationPlan.planImages ?? state.plans.masterPlan.planImages;
        case "master_plan":
          return state.plans.masterPlan.planImages;
      }
    }
  }
}
